/**
 * Auth against the backend's self-hosted endpoints. The session lives in
 * httpOnly cookies; this just tracks/reports state and drives login/logout.
 *
 * It also keeps the access token fresh *ahead* of expiry. api.js can recover
 * from a 401 by refreshing and replaying, but that only covers requests
 * JavaScript makes: gallery photos are plain <img> tags issued by the browser,
 * so an expired cookie makes them fail with nothing to catch and retry. Renewing
 * before the token lapses keeps the cookie valid for every kind of request.
 */
import api from "./api.js";

// Renew at 80% of the token's life, leaving headroom for a slow or failed
// attempt while the current token is still usable.
const RENEWAL_POINT = 0.8;
// Timers in background tabs are throttled and do not fire reliably, so a tab
// returning to the foreground re-checks instead of trusting its schedule.
const STALE_AFTER_HIDDEN_MS = 30_000;

class AuthService {
  constructor() {
    this.user = null;
    this.renewalTimer = null;
    this.lastRenewedAt = 0;
    this.watchTabVisibility();
  }

  /** Ask the backend who we are, and schedule the next renewal from its answer. */
  async refresh() {
    try {
      const res = await api.get("/api/auth/me");
      this.user = res?.user || null;
      if (this.user) this.scheduleRenewal(res?.accessTokenExpiresInSeconds);
      else this.cancelRenewal();
    } catch {
      this.user = null;
      this.cancelRenewal();
    }
    return this.user;
  }

  isAuthenticated() {
    return Boolean(this.user);
  }

  isAdmin() {
    return Boolean(this.user?.admin);
  }

  /**
   * Password step. When the account has a second factor the backend issues NO
   * session — it returns a short-lived challenge instead. Returns
   * { totpRequired: true, challenge } in that case so the caller can prompt for
   * a code; the user is only set once completeTotpLogin succeeds.
   */
  async login(email, password) {
    const res = await api.post("/api/auth/login", { email, password });
    if (res?.totpRequired) return { totpRequired: true, challenge: res.challenge };

    this.user = res.user;
    this.scheduleRenewal(res?.accessTokenExpiresInSeconds);
    return this.user;
  }

  /**
   * Second step: exchange the challenge and a code for a real session. Pass
   * either `code` (from the authenticator) or `recoveryCode`.
   */
  async completeTotpLogin(challenge, { code, recoveryCode } = {}) {
    const res = await api.post("/api/auth/login/totp", { challenge, code, recoveryCode });
    this.user = res.user;
    this.scheduleRenewal(res?.accessTokenExpiresInSeconds);
    return { user: this.user, recoveryCodesRemaining: res?.recoveryCodesRemaining };
  }

  async logout() {
    this.cancelRenewal();
    await api.post("/api/auth/logout");
    this.user = null;
  }

  // --- keeping the access token fresh ---------------------------------------

  cancelRenewal() {
    if (this.renewalTimer) clearTimeout(this.renewalTimer);
    this.renewalTimer = null;
  }

  /** Queue a renewal for shortly before the current token lapses. */
  scheduleRenewal(expiresInSeconds) {
    this.cancelRenewal();
    if (!expiresInSeconds || expiresInSeconds <= 0) return;

    // setTimeout takes a 32-bit delay; anything longer fires immediately.
    const delayMs = Math.min(expiresInSeconds * RENEWAL_POINT * 1000, 2 ** 31 - 1);
    this.renewalTimer = setTimeout(() => this.renewNow(), delayMs);
  }

  /** Renew now and re-arm. On failure the session is over; api.js reports it. */
  async renewNow() {
    if (!this.user) return;
    const renewed = await api.renewSession();
    this.lastRenewedAt = Date.now();
    if (renewed) await this.refresh();
    else this.cancelRenewal();
  }

  watchTabVisibility() {
    if (typeof document === "undefined") return;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState !== "visible" || !this.user) return;
      // A laptop that slept through the scheduled renewal wakes with a token
      // that may already be dead, and the first thing the user does is look at
      // images. Re-check rather than wait for the throttled timer.
      if (Date.now() - this.lastRenewedAt > STALE_AFTER_HIDDEN_MS) this.renewNow();
    });
  }
}

export default new AuthService();
